import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from '../api/api.service';

export interface PendingApprovalSummary {
  department_pending: number;
  total_items: number;
  area?: string;
  by_role?: {
    provider: number;
    assignor: number;
    shareholder: number;
    total: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class PendingApprovalService implements OnDestroy {
  private summarySubject = new BehaviorSubject<PendingApprovalSummary | null>(
    null
  );
  public summary$: Observable<PendingApprovalSummary | null> =
    this.summarySubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private intervalId?: any;
  private readonly UPDATE_INTERVAL = 60000; // 1 minuto em milissegundos
  private readonly REQUEST_TIMEOUT = 10000; // 10 segundos de timeout
  private isFetching = false; // Flag para evitar chamadas simultâneas

  constructor(private api: ApiService) {
    // Usar setTimeout para não bloquear o carregamento inicial
    // Chama após um pequeno delay para não interferir com outras inicializações
    setTimeout(() => {
      this.fetchPendingCount();
    }, 100);
    
    // Inicia o intervalo de atualização automática
    this.startAutoUpdate();
  }

  /**
   * Busca a contagem de aprovações pendentes da API
   * Esta função é separada e não causa loading na tabela
   */
  async fetchPendingCount(): Promise<void> {
    // Evitar múltiplas chamadas simultâneas
    if (this.isFetching) {
      return;
    }

    this.isFetching = true;
    this.loadingSubject.next(true);

    try {
      // Criar uma promise com timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Request timeout'));
        }, this.REQUEST_TIMEOUT);
      });

      const apiPromise = this.api.get<{
        data: PendingApprovalSummary;
      }>({
        route: 'api/approvals/v3/user_approval/my-area-dashboard/summary/',
        token: true,
      });

      // Race entre a chamada da API e o timeout
      const { data } = await Promise.race([apiPromise, timeoutPromise]);

      if (data) {
        this.summarySubject.next(data);
      }
    } catch (error) {
      console.error('Error fetching pending approval count:', error);
      // Em caso de erro, mantém o último valor ou define como null
      // Não atualiza para não perder a informação anterior
      // Só define como null se não houver valor anterior
      if (!this.summarySubject.value) {
        this.summarySubject.next(null);
      }
    } finally {
      this.isFetching = false;
      this.loadingSubject.next(false);
    }
  }

  /**
   * Inicia a atualização automática a cada 1 minuto
   */
  private startAutoUpdate(): void {
    this.intervalId = setInterval(() => {
      // Só atualiza se não estiver já buscando
      if (!this.isFetching) {
        this.fetchPendingCount();
      }
    }, this.UPDATE_INTERVAL);
  }

  /**
   * Para a atualização automática
   */
  stopAutoUpdate(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  /**
   * Reinicia a atualização automática
   */
  restartAutoUpdate(): void {
    this.stopAutoUpdate();
    this.startAutoUpdate();
  }

  /**
   * Obtém o valor atual do summary (último valor recebido)
   */
  getCurrentSummary(): PendingApprovalSummary | null {
    return this.summarySubject.value;
  }

  /**
   * Força uma atualização manual (útil após ações que podem mudar a contagem)
   */
  async refresh(): Promise<void> {
    await this.fetchPendingCount();
  }

  ngOnDestroy(): void {
    this.stopAutoUpdate();
  }
}

